import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react'

/**
 * Emergency auth recovery page for when OAuth tokens are received but not accepted
 * This manually extracts tokens from URL and forces a session
 */
const AuthForceLogin = () => {
	const navigate = useNavigate()
	const { toast } = useToast()
	const [status, setStatus] = useState<
		'idle' | 'processing' | 'success' | 'error'
	>('idle')
	const [errorMessage, setErrorMessage] = useState('')
	const [tokens, setTokens] = useState<{ access?: string; refresh?: string }>(
		{}
	)

	useEffect(() => {
		// Check if we have tokens in the URL
		const hash = window.location.hash
		if (hash && hash.includes('access_token')) {
			const hashParams = new URLSearchParams(hash.substring(1))
			const accessToken = hashParams.get('access_token')
			const refreshToken = hashParams.get('refresh_token')

			if (accessToken && refreshToken) {
				setTokens({
					access: `${accessToken.substring(0, 20)}...`,
					refresh: `${refreshToken.substring(0, 10)}...`,
				})
			}
		}
	}, [])

	const forceLogin = async () => {
		setStatus('processing')
		setErrorMessage('')

		try {
			// Get tokens from URL hash
			const hash = window.location.hash
			if (!hash || !hash.includes('access_token')) {
				throw new Error('No OAuth tokens found in URL')
			}

			const hashParams = new URLSearchParams(hash.substring(1))
			const accessToken = hashParams.get('access_token')
			const refreshToken = hashParams.get('refresh_token')

			if (!accessToken || !refreshToken) {
				throw new Error('Missing access or refresh token')
			}

			// Force set the session
			const { data, error } = await supabase.auth.setSession({
				access_token: accessToken,
				refresh_token: refreshToken,
			})

			if (error) throw error

			if (data?.session) {
				setStatus('success')
				toast({
					title: 'Success!',
					description: 'Session established successfully. Redirecting...',
				})

				// Clear the hash
				window.location.hash = ''

				// Redirect to dashboard after a short delay
				setTimeout(() => {
					navigate('/dashboard', { replace: true })
				}, 1500)
			} else {
				throw new Error('Session was not established')
			}
		} catch (error: any) {
			setStatus('error')
			setErrorMessage(error.message || 'Failed to establish session')
			console.error('[AuthForceLogin] Error:', error)
		}
	}

	const copyUrl = () => {
		const url = `${window.location.origin}/auth/callback`
		navigator.clipboard.writeText(url)
		toast({
			title: 'Copied!',
			description: 'URL copied to clipboard',
		})
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
			<Card className="w-full max-w-lg">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<AlertCircle className="h-5 w-5 text-yellow-500" />
						OAuth Recovery Tool
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					{tokens.access && (
						<div className="p-3 bg-gray-100 rounded-lg text-xs font-mono">
							<p className="text-gray-600">Tokens detected:</p>
							<p>Access: {tokens.access}</p>
							<p>Refresh: {tokens.refresh}</p>
						</div>
					)}

					{status === 'idle' && (
						<>
							<p className="text-sm text-gray-600">
								This tool will manually establish your session using OAuth
								tokens from the URL.
							</p>
							<Button onClick={forceLogin} className="w-full">
								Force Login with Tokens
							</Button>
						</>
					)}

					{status === 'processing' && (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="h-8 w-8 animate-spin text-primary" />
						</div>
					)}

					{status === 'success' && (
						<div className="flex flex-col items-center gap-3 py-8">
							<CheckCircle className="h-12 w-12 text-green-500" />
							<p className="text-green-600 font-medium">Session established!</p>
							<p className="text-sm text-gray-600">
								Redirecting to dashboard...
							</p>
						</div>
					)}

					{status === 'error' && (
						<div className="space-y-3">
							<div className="p-3 bg-red-50 border border-red-200 rounded-lg">
								<p className="text-sm text-red-600">{errorMessage}</p>
							</div>
							<Button onClick={forceLogin} variant="outline" className="w-full">
								Try Again
							</Button>
						</div>
					)}

					<div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
						<h4 className="font-semibold text-sm mb-2">Fix the root cause:</h4>
						<ol className="text-xs space-y-1 list-decimal list-inside">
							<li>Go to Supabase Dashboard → Auth → URL Configuration</li>
							<li>
								Remove wildcard URLs like{' '}
								<code className="bg-yellow-100 px-1">
									http://localhost:8080/**
								</code>
							</li>
							<li>
								Add this exact URL:
								<code className="bg-yellow-100 px-1 ml-1">
									{window.location.origin}/auth/callback
								</code>
								<Button
									size="sm"
									variant="ghost"
									onClick={copyUrl}
									className="ml-2 h-6 px-2 text-xs">
									Copy
								</Button>
							</li>
							<li>Save changes and wait 30 seconds</li>
						</ol>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}

export default AuthForceLogin
